<template>
  <CModal :show.sync="show" :title="$pgettext('browse_photo', 'Filter')" centered size="lg">
    <PComponentLoader :loading="loading">
      <template v-if="!loading">
        <!-- Media Filters Section -->
        <div class="filter-section">
          <h6 class="filter-section__title">
            <CIcon name="cipPicture" size="custom-size" :height="20" class="mr-2" />
            <translate translate-context="filter_modal.section">Media Filters</translate>
          </h6>

          <PFRow :label="$pgettext('photo_search.label','Photographer')"
                 size="12x12"
                 v-if="permissions.canFilterByPhotographers">
            <PSelectKeyValue
                :options="options.photographers"
                :value.sync="value.photographer_id"
                allow-empty
            />
          </PFRow>
          <PFRow :label="$pgettext('photo_search.label','Tags')"
                 size="12x12"
                 v-if="permissions.canFilterByTags && options.tags.length">
            <PTagInput v-model="value.tags" :allowed_tags="options.tags"/>
          </PFRow>
          <PFRow :label="$pgettext('photo_search.label','Date')"
                  size="12x12"
                 v-if="permissions.canFilterByShotAt"
          >
            <VueCtkDateTimePicker
                v-model="value.date_range"
                :no-label="true"
                :range="true"
                format="YYYY-MM-DD HH:mm"
                formatted="LLL"
                color="#1867FF"
                 class="mb-2"
            />
          </PFRow>
          <PFRow
              size="12x12"
              :label="$pgettext('photo_search.label','Pica Code')"
              v-if="permissions.canFilterByPicaCode"
          >
            <CInput v-model="value.pica_code" :placeholder="$pgettext('photo_search.placeholder','Pica Code')"/>
          </PFRow>
          <PFRow
              size="12x12"
              :label="$pgettext('photo_search.label','Race Number')"
              v-if="permissions.canFilterByRaceNumber"
          >
            <CInput v-model="value.race_number" :placeholder="$pgettext('photo_search.placeholder','Race Number')"/>
          </PFRow>
          <PFRow
              size="12x12"
              :label="$pgettext('photo_search.label','Delivery Status')"
              v-if="permissions.canFilterByDeliveryStatus"
          >
            <PSelectKeyValue
                :options="deliveryStatusOptions"
                :value.sync="value.delivery_status"
                allow-empty
            />
          </PFRow>

          <!-- Dynamic Media Tag Filters -->
          <DynamicTagField
            v-for="tag in mediaTagDefinitions"
            :key="tag.code"
            :tagDefinition="tag"
            :value="getTagValue(tag.code)"
            @input="setTagValue(tag.code, $event)"
          />
        </div>

        <!-- Person Filters Section -->
        <div class="filter-section" v-if="clusterTagDefinitions.length > 0">
          <h6 class="filter-section__title">
             <CIcon name="cipUsers" size="custom-size" :height="20" class="mr-2" />
            <translate translate-context="filter_modal.section">Cluster Filters</translate>
          </h6>

          <DynamicTagField
            v-for="tag in clusterTagDefinitions"
            :key="tag.code"
            :tagDefinition="tag"
            :value="getTagValue(tag.code)"
            @input="setTagValue(tag.code, $event)"
          />
        </div>
      </template>
    </PComponentLoader>
    <template #footer>
      <CButton color="secondary" @click="show = false" v-translate>Close</CButton>
      <CButton color="primary" @click="onFilter" v-translate>Search</CButton>
    </template>
  </CModal>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {cloneDeep} from "lodash";
import PComponentLoader from "@/domain/core/components/PComponentLoader.vue";
import PFRow from "@/domain/core/components/PFRow.vue";
import PSelectKeyValue from "@/domain/core/components/PSelectKeyValue.vue";
import PTagInput from "@/domain/core/components/PTagInput.vue";
import DynamicTagField from "@/domain/photoSearch/components/DynamicTagField.vue";
import deliveryStatus from "@/domain/core/constant/deliveryStatus";
import {TAG_SCOPE_VALUE} from "@/domain/core/constant/tagScope";

export default {
  name: "FilterModal",
  components: {DynamicTagField, PTagInput, PSelectKeyValue, PFRow, PComponentLoader},
  emits: ['filter'],
  computed: {
    deliveryStatusOptions() {
      return deliveryStatus.options()
    },
    mediaTagDefinitions() {
      if (!this.tagDefinitions) return []
      return this.tagDefinitions.filter(def =>
        def.scopes && def.scopes.includes(TAG_SCOPE_VALUE.MEDIA)
      )
    },
    clusterTagDefinitions() {
      if (!this.tagDefinitions) return []
      return this.tagDefinitions.filter(def =>
        def.scopes && def.scopes.includes(TAG_SCOPE_VALUE.CLUSTER)
      )
    },
    ...mapGetters('photoSearch', [
      'permissions',
      'filters',
      'options',
      'tagDefinitions'
    ]),
  },
  data() {
    return {
      show: false,
      loading: true,
      value: {
        photographer_id: null,
        tags: null,
        pica_code: null,
        date_range: {
          start: null,
          end: null
        },
        delivery_status: null,
      },
    }
  },
  methods: {
    ...mapActions('photoSearch', [
      'fetchFilterOptions',
      'fetchTagDefinitions'
    ]),
    async open() {
      this.value = cloneDeep(this.filters)
      this.show = true

      // Fetch both filter options and tag definitions
      const promises = []
      if (!this.options)
        promises.push(this.fetchFilterOptions())
      if (this.permissions.canArchive && this.tagDefinitions.length === 0)
        promises.push(this.fetchTagDefinitions())

      if (promises.length > 0)
        await Promise.all(promises)

      this.loading = false
    },
    getTagValue(code) {
      return this.value[`tag_${code}`]
    },
    setTagValue(code, value) {
      if (value === null || value === '' || value === undefined) {
        this.$delete(this.value, `tag_${code}`)
      } else {
        this.$set(this.value, `tag_${code}`, value)
      }
    },
    async onFilter() {
      await this.emitPromised('filter', this.value)
      this.show = false
    }
  }
}
</script>

<style scoped>
.filter-section {
  margin-bottom: 1.5rem;
}

.filter-section:last-child {
  margin-bottom: 0;
}

.filter-section__title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #4f5d73;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #d8dbe0;
}
</style>